"use client";

/**
 * FinanceMock — animated mock of the askFinz Finance markets terminal for
 * the /apps/finance marketing page.
 *
 * Pure-presentation, NO network and NO real data: an index strip, a price
 * chart that draws itself in (area + line via stroke-dashoffset), a
 * watchlist / quote board whose prices tick live-looking up and down with a
 * green/red flash, and a compact AI "read of the tape" line that types
 * itself in. Wired to an IntersectionObserver so it animates on scroll.
 *
 * Trademark guardrail: generic ticker symbols only — nothing that implies a
 * specific exchange, company or data source. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Sparkles,
  CandlestickChart,
  Activity,
} from "lucide-react";

/* ────────── Static seed data ────────── */

type Quote = {
  ticker: string;
  name: string;
  price: number;
  pct: number; // day change %
  up: boolean;
};

const SEED_QUOTES: Quote[] = [
  { ticker: "AVX",  name: "Avalon Index ETF",  price: 482.16, pct: 1.24,  up: true },
  { ticker: "NRG",  name: "Northgate Energy",  price: 71.43,  pct: -0.62, up: false },
  { ticker: "QBT",  name: "Quanta Bio",        price: 318.90, pct: 2.18,  up: true },
  { ticker: "HLX",  name: "Helix Semi",        price: 144.07, pct: 0.71,  up: true },
  { ticker: "MRD",  name: "Meridian Funds",    price: 58.22,  pct: -0.34, up: false },
  { ticker: "ORB",  name: "Orbit Crypto",      price: 9_412.5, pct: 3.05, up: true },
];

const INDICES = [
  { label: "World 500", value: "5,184.20", pct: "+0.84%", up: true },
  { label: "Tech 100",  value: "18,902.7", pct: "+1.31%", up: true },
  { label: "EM Broad",  value: "2,431.55", pct: "-0.22%", up: false },
  { label: "Vol",       value: "13.4",      pct: "-2.10%", up: false },
];

// Chart path — a drawn-in price line for the focused name (AVX).
const CHART = [
  38, 40, 39, 43, 45, 44, 47, 46, 50, 53, 51, 55, 57, 56, 60,
  62, 61, 65, 64, 68, 70, 69, 72, 75, 74, 78, 80, 79, 83, 86,
];

const AI_READ =
  "AVX is leading on broad-tech strength; breadth is positive, with risk skewed to the 472 support. Forecast: 488–501 over 5 sessions, 64% confidence.";

/* ────────── Component ────────── */

export function FinanceMock() {
  const [quotes, setQuotes] = useState<Quote[]>(SEED_QUOTES);
  const [flash, setFlash] = useState<Record<string, "up" | "down" | undefined>>({});
  const [chartDrawn, setChartDrawn] = useState(false);
  const [readChars, setReadChars] = useState(0);
  const [focus, setFocus] = useState({ price: 482.16, pct: 1.24, chg: 5.92 });
  const [tape, setTape] = useState<{ p: string; up: boolean }[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    let tick: number | undefined;

    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const run = () => {
      // Draw the chart in.
      schedule(() => setChartDrawn(true), 250);

      // Type the AI read.
      const total = AI_READ.length;
      const frames = 46;
      for (let f = 1; f <= frames; f++) {
        schedule(() => setReadChars(Math.ceil((total * f) / frames)), 900 + f * 26);
      }
      schedule(() => setReadChars(total), 900 + frames * 26 + 120);

      // Live-looking quote ticking — jitter one random row periodically.
      tick = window.setInterval(() => {
        // jitter two random watchlist rows each tick for a livelier board
        setQuotes((prev) => {
          const copy = prev.slice();
          for (let k = 0; k < 2; k++) {
            const idx = Math.floor(Math.random() * copy.length);
            const q = copy[idx];
            const drift = (Math.random() - 0.45) * (q.price * 0.0016);
            const nextPrice = Math.max(0.01, q.price + drift);
            const nextPct = q.pct + drift / q.price * 100;
            const dir: "up" | "down" = drift >= 0 ? "up" : "down";
            setFlash((fl) => ({ ...fl, [q.ticker]: dir }));
            window.setTimeout(() => setFlash((fl) => ({ ...fl, [q.ticker]: undefined })), 520);
            copy[idx] = { ...q, price: nextPrice, pct: nextPct, up: nextPct >= 0 };
          }
          return copy;
        });
        // drift the focused-name price + print to the tape
        setFocus((f) => {
          const drift = (Math.random() - 0.42) * 0.5;
          const price = Math.max(0.01, f.price + drift);
          const chg = f.chg + drift;
          const pct = price - chg > 0 ? (chg / (price - chg)) * 100 : f.pct;
          setTape((t) => [{ p: price.toFixed(2), up: drift >= 0 }, ...t].slice(0, 8));
          return { price, pct, chg };
        });
      }, 820);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        run();
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
      if (tick) clearInterval(tick);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            finance.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Markets open
          </span>
        </div>

        {/* Index strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-foreground/[0.08] border-b hairline">
          {INDICES.map((ix) => (
            <div key={ix.label} className="bg-card px-4 py-2.5">
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65">{ix.label}</div>
              <div className="mt-0.5 flex items-baseline gap-2">
                <span className="font-mono text-[15px] tracking-tight text-foreground/90">{ix.value}</span>
                <span className={"font-mono text-[11px] " + (ix.up ? "text-emerald-500" : "text-rose-500")}>
                  {ix.pct}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-12 min-h-[420px]">
          {/* Chart */}
          <div className="col-span-12 md:col-span-7 border-r hairline flex flex-col">
            <div className="px-4 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <CandlestickChart className="h-3 w-3" />
              AVX · Avalon Index ETF
              <span className="ml-auto font-mono normal-case tracking-normal text-foreground/65">1D · 30+ studies</span>
            </div>
            <div className="flex items-baseline gap-3 px-4 pt-3">
              <span className="font-mono text-[26px] tracking-tight text-foreground/90 tabular-nums">{focus.price.toFixed(2)}</span>
              <span className={"inline-flex items-center gap-1 font-mono text-[13px] tabular-nums " + (focus.chg >= 0 ? "text-emerald-500" : "text-rose-500")}>
                {focus.chg >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {(focus.chg >= 0 ? "+" : "") + focus.pct.toFixed(2)}% · {(focus.chg >= 0 ? "+" : "") + focus.chg.toFixed(2)}
              </span>
            </div>
            <div className="flex-1 px-2 pb-2 pt-3">
              <PriceChart values={CHART} drawn={chartDrawn} />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 border-t hairline text-[10px] text-foreground/55 font-mono">
              <span className="rounded-sm border hairline px-1.5 py-0.5">RSI 58</span>
              <span className="rounded-sm border hairline px-1.5 py-0.5">MACD +</span>
              <span className="rounded-sm border hairline px-1.5 py-0.5">VWAP 478.4</span>
              <span className="ml-auto flex items-center gap-1.5 overflow-hidden" aria-hidden>
                {tape.slice(0, 6).map((t, i) => (
                  <span key={tape.length - i} className={"tabular-nums " + (t.up ? "text-emerald-500" : "text-rose-500")} style={{ opacity: 1 - i * 0.14 }}>{t.p}</span>
                ))}
              </span>
            </div>
          </div>

          {/* Watchlist / quote board */}
          <div className="col-span-12 md:col-span-5 flex flex-col bg-foreground/[0.015]">
            <div className="px-4 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Activity className="h-3 w-3" />
              Watchlist · {quotes.length} names
              <span className="ml-auto font-mono normal-case tracking-normal text-foreground/65">live</span>
            </div>
            <div className="divide-y divide-foreground/[0.06]">
              {quotes.map((q) => {
                const fl = flash[q.ticker];
                return (
                  <div
                    key={q.ticker}
                    className={
                      "grid grid-cols-[repeat(12,minmax(0,1fr))] items-center gap-2 px-4 py-3 text-[12px] transition-colors duration-500 " +
                      (fl === "up"
                        ? "bg-emerald-500/[0.07]"
                        : fl === "down"
                        ? "bg-rose-500/[0.07]"
                        : "")
                    }
                  >
                    <div className="col-span-5 min-w-0">
                      <div className="font-mono text-foreground/85">{q.ticker}</div>
                      <div className="text-[10px] text-foreground/55 truncate">{q.name}</div>
                    </div>
                    <div className="col-span-4 text-right font-mono text-foreground/85 tabular-nums">
                      {q.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div
                      className={
                        "col-span-3 text-right font-mono tabular-nums " +
                        (q.up ? "text-emerald-500" : "text-rose-500")
                      }
                    >
                      <span className="inline-flex items-center gap-0.5">
                        {q.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {(q.up ? "+" : "") + q.pct.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* AI read of the tape */}
            <div className="mt-auto border-t hairline p-4 bg-foreground/[0.02]">
              <div className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                <Sparkles className="h-3 w-3" />
                AI read of the tape
              </div>
              <p className="mt-2 text-[12px] leading-[1.6] text-foreground/85 min-h-[3.6em]">
                {AI_READ.slice(0, readChars)}
                {readChars < AI_READ.length && (
                  <span className="inline-block h-3 w-1 bg-foreground/55 align-middle ml-0.5 animate-pulse" />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────── Bits ────────── */

/** Drawn-in price line with a soft area fill below it. */
function PriceChart({ values, drawn }: { values: number[]; drawn: boolean }) {
  const w = 320, h = 150, pad = 4;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  const stepX = (w - pad * 2) / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return [x, y] as const;
  });
  const line = "M" + pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L");
  const area =
    line +
    ` L${pts[pts.length - 1][0].toFixed(1)},${h - pad} L${pts[0][0].toFixed(1)},${h - pad} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" preserveAspectRatio="none" className="block text-emerald-500">
      <defs>
        <linearGradient id="fin-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* gridlines */}
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1={0} x2={w} y1={h * g} y2={h * g} stroke="currentColor" strokeOpacity="0.06" strokeWidth="1" />
      ))}
      {/* area fill, fades in after the line draws */}
      <path
        d={area}
        fill="url(#fin-area)"
        style={{ opacity: drawn ? 1 : 0, transition: "opacity 0.8s ease-out 0.5s" }}
      />
      {/* the line itself */}
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{
          strokeDasharray: 900,
          strokeDashoffset: drawn ? 0 : 900,
          transition: "stroke-dashoffset 1.4s ease-out",
        }}
      />
    </svg>
  );
}
